import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import axios from 'axios';
import { Trophy, Clock, CheckCircle, XCircle, Coins, TrendingUp, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusBadge = ({ status }) => {
  if (status === 'completed') {
    return (
      <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">
        <CheckCircle className="w-3 h-3" />
        <span>Completed</span>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-bold animate-pulse">
      <Clock className="w-3 h-3" />
      <span>Active</span>
    </div>
  );
};

const MyContests = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const fetchContests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/my-contests`);
      setContests(data);
    } catch (error) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContests();
  }, [fetchContests]);

  const filteredContests = contests.filter(c => {
    if (tab === 'active') return c.status === 'active';
    if (tab === 'completed') return c.status === 'completed';
    return true;
  });

  const totalPrizesWon = contests.reduce((sum, c) => sum + (c.prize_won || 0), 0);
  const totalPlayed = contests.filter(c => c.status === 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0EA5E9]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="my-contests-page">
      <Header />

      <main className="max-w-7xl mx-auto px-6 py-12">
        <h1
          className="text-4xl sm:text-5xl font-black text-[#111827] mb-8"
          style={{ fontFamily: 'Fredoka, sans-serif' }}
        >
          My Contests
        </h1>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="quiz-card p-5">
            <p className="text-sm text-[#6B7280] mb-1">Total Entered</p>
            <p className="text-3xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {contests.length}
            </p>
          </div>
          <div className="quiz-card p-5">
            <p className="text-sm text-[#6B7280] mb-1">Completed</p>
            <p className="text-3xl font-black text-[#0EA5E9]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {totalPlayed}
            </p>
          </div>
          <div className="quiz-card p-5">
            <p className="text-sm text-[#6B7280] mb-1">Active</p>
            <p className="text-3xl font-black text-[#FBBF24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {contests.length - totalPlayed}
            </p>
          </div>
          <div className="quiz-card p-5">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-[#FBBF24]" />
              <p className="text-sm text-[#6B7280]">Total Won</p>
            </div>
            <p className="text-3xl font-black gradient-text" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {totalPrizesWon.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Tab Filter */}
        <div className="flex gap-2 mb-8">
          {['all', 'active', 'completed'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-full font-bold text-sm capitalize transition-all ${
                tab === t
                  ? 'bg-[#0EA5E9] text-white shadow-lg'
                  : 'bg-white text-[#6B7280] border-2 border-[#E5E7EB] hover:border-[#0EA5E9]'
              }`}
              data-testid={`tab-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Contest List */}
        {filteredContests.length === 0 ? (
          <div className="text-center py-20 quiz-card">
            <XCircle className="w-16 h-16 text-[#E5E7EB] mx-auto mb-4" />
            <p className="text-[#6B7280] text-lg mb-4">No contests found</p>
            <button
              onClick={() => navigate('/quiz-pools')}
              className="px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold"
              data-testid="browse-pools-button"
            >
              Browse Quiz Pools
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredContests.map((contest) => (
              <div
                key={contest.pool_id}
                className="quiz-card p-6 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => {
                  if (contest.status === 'completed') {
                    navigate(`/leaderboard/${contest.pool_id}`);
                  } else {
                    navigate(`/quiz/${contest.pool_id}`);
                  }
                }}
                data-testid={`contest-${contest.pool_id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                      contest.pool_type === 'mega'
                        ? 'bg-gradient-to-br from-[#FF6B6B] to-[#EE5A52]'
                        : 'bg-gradient-to-br from-[#0EA5E9] to-[#0284C7]'
                    }`}>
                      {contest.pool_type === 'mega'
                        ? <Crown className="w-7 h-7 text-white" />
                        : <Trophy className="w-7 h-7 text-white" />
                      }
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-bold text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                          {contest.subject}
                        </h3>
                        {contest.pool_type === 'mega' && (
                          <span className="px-2 py-0.5 bg-[#FF6B6B] text-white rounded-full text-xs font-bold">MEGA</span>
                        )}
                      </div>
                      <p className="text-sm text-[#6B7280]">{contest.topic}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {contest.status === 'completed' && (
                      <>
                        {/* Score */}
                        <div className="text-center">
                          <p className="text-xs text-[#6B7280]">Score</p>
                          <p className="text-xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            {contest.score}/{contest.total_questions}
                          </p>
                        </div>

                        {/* Rank */}
                        <div className="text-center">
                          <p className="text-xs text-[#6B7280]">Rank</p>
                          <p className={`text-xl font-black ${
                            contest.rank <= 3 ? 'text-[#FBBF24]' : 'text-[#111827]'
                          }`} style={{ fontFamily: 'Fredoka, sans-serif' }}>
                            #{contest.rank}
                          </p>
                        </div>

                        {/* Prize */}
                        {contest.prize_won > 0 && (
                          <div className="flex items-center gap-1 px-3 py-2 bg-gradient-to-r from-[#FBBF24] to-[#F59E0B] rounded-xl">
                            <Coins className="w-4 h-4 text-white" />
                            <span className="font-bold text-white">+{contest.prize_won.toLocaleString()}</span>
                          </div>
                        )}
                      </>
                    )}

                    <StatusBadge status={contest.status} />

                    <TrendingUp className="w-5 h-5 text-[#6B7280]" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MyContests;
